// **The score's delta against the player's previous run** (`client-sandbox-screen`, task 5.1a,
// artifacts review **B4**).
//
// This is the iteration feedback the whole editing loop exists to give. A bare score answers
// "how did that go"; only the delta answers "did my change help", which is the question a
// player is actually holding when they press Run a second time. The guarantee used to live on
// the completion toast the consolidation retires; **B4** caught that the removal claimed to
// migrate it and did not, so it is re-homed here — as a derivation rather than as a string
// built at the readout, because the interesting rule is not arithmetic.
//
// The interesting rule is the **first run**. A player's first run has no predecessor, and
// differencing against `0` would render `+438` — a rise from a run that never happened, and on
// a decline the reverse: a collapse out of nowhere. So the absence of a previous run is a
// **case in the type**, not a zero and not a `null` change, and a readout that has not handled
// it cannot compile. `undefined`/`null` would have left every caller free to format the
// absence as a number, which is precisely the failure mode.
//
// Everything here rounds to whole bees first, for the same reason the counters do
// (`client-sandbox-screen`: "rounded to the nearest integer"): the G1 metric is a population,
// and a pill reading `438` beside a badge reading `+51.6` is two views of one number
// disagreeing.
//
// Pure: numbers in, plain data out. No React, no DOM, no formatting of the *score* itself —
// that belongs to the pill (`components/hud/ResourceCounter.tsx`), and this module deliberately
// does not import it, so the model never depends on a component.

/** Which way the run moved. `level` is a real answer — the player changed nothing that mattered. */
export type DeltaDirection = 'up' | 'down' | 'level';

/** A run with a predecessor to be measured against. */
export interface ScoreDelta {
  readonly kind: 'delta';
  /** Signed whole-bee difference: current − previous. */
  readonly change: number;
  readonly direction: DeltaDirection;
  /** The score compared against, kept so a readout can name it without re-deriving it. */
  readonly previousScore: number;
  /** The compact badge text: `+52`, `−52` (U+2212), `±0`. */
  readonly label: string;
  /** The same fact as a sentence, for the badge's accessible name. */
  readonly description: string;
}

/**
 * No previous run, so **no delta is presented at all**. A distinct case rather than a zero
 * delta: a first run is not a run that scored the same, and it is not a rise from nothing.
 */
export interface FirstRun {
  readonly kind: 'first-run';
}

export type ScoreComparison = ScoreDelta | FirstRun;

/** The single first-run value. Shared so an identity check is a legitimate way to spot it. */
export const FIRST_RUN: FirstRun = { kind: 'first-run' };

/** One run, as this model reads it — structurally satisfied by `version-menu-model`'s
 * `MenuCandidate` and by `rounds-http`'s `RunDto`, without either importing the other. */
export interface ScoredRun {
  readonly runId: number;
  /** The G1 score, or `null` while the run has not produced one. Never coerced to a number. */
  readonly score: number | null;
}

/** Whole bees, grouped the way the counter pills group theirs. */
function grouped(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/**
 * The score on screen measured against the previous run's, or {@link FIRST_RUN} when there is
 * nothing to measure against.
 *
 * A non-finite score on either side is treated as an absence rather than compared: `NaN` would
 * render as `+NaN` and `Infinity` as a triumph, and both are absences wearing a number — the
 * screen's rule is that a value it cannot source is stated as missing, never fabricated.
 */
export function scoreComparison(currentScore: number, previousScore: number | null): ScoreComparison {
  if (previousScore === null || !Number.isFinite(previousScore) || !Number.isFinite(currentScore)) {
    return FIRST_RUN;
  }

  const previous = Math.round(previousScore);
  const change = Math.round(currentScore) - previous;
  if (change === 0) {
    return {
      kind: 'delta',
      change,
      direction: 'level',
      previousScore: previous,
      label: '±0',
      description: 'the same as your previous run',
    };
  }

  const up = change > 0;
  const magnitude = grouped(Math.abs(change));
  return {
    kind: 'delta',
    change,
    direction: up ? 'up' : 'down',
    previousScore: previous,
    // U+2212 MINUS SIGN, not a hyphen: it sits on the same optical axis as the `+` and matches
    // the digit weight beside it, which a hyphen at this size does not.
    label: up ? `+${magnitude}` : `−${magnitude}`,
    description: `${magnitude} ${up ? 'more' : 'fewer'} than your previous run`,
  };
}

/**
 * The score of the most recent run recorded **before** `runId`, or `null` when this is the
 * sandbox's first scored run.
 *
 * A run that produced no score is skipped rather than read as a zero — "no answer yet" and
 * "no bees left" are different facts, and `version-menu-model` keeps them apart for the same
 * reason. `runId` order is the recording order the run surface assigns, so this needs no clock.
 */
export function previousScoreBefore(runs: readonly ScoredRun[], runId: number): number | null {
  let bestId = Number.NEGATIVE_INFINITY;
  let bestScore: number | null = null;
  for (const run of runs) {
    if (run.runId >= runId || run.score === null || !Number.isFinite(run.score)) {
      continue;
    }
    if (run.runId > bestId) {
      bestId = run.runId;
      bestScore = run.score;
    }
  }
  return bestScore;
}
